<script setup lang="ts">
import { computed, inject, Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { richTextToGovukHtml } from '/@src/utils/richTextToGovukHtml'
import { PovinnaPrilohaPublicResponse } from '/@src/client-public'
import { useFieldArray, useFormValues } from 'vee-validate'
import * as apisPortal from '/@src/client-portal/api'
import { useNotyf } from '/@src/composable/useNotyf'
import FileDownload from 'js-file-download'
import { Dokument, ZoPPMPriloha } from '/@src/client-portal'
import { MAX_FILE_SIZE_MB } from '/@src/constants/index'
import { checkFiles } from '/@src/utils/checkFiles'

export interface PreddefinedAttachmentProps {
  priloha: PovinnaPrilohaPublicResponse
}

const props = defineProps<PreddefinedAttachmentProps>()
const { t } = useI18n()
const notif = useNotyf()
const {
  fields: fieldPrilohy,
  push: pushToPrilohy,
  update: updatePrilohy,
  remove: removePrilohy,
} = useFieldArray(`prilohy`)

const api = new apisPortal.ZoppmDraftApi()
const kodIrz = inject<Ref<string>>('kodIrz')
const isReadOnly = inject<boolean>('isReadOnly')
const formValues = useFormValues()
const isChecked = ref(false)
const loadingFile = ref<boolean>(false)

const idx = computed(() =>
  formValues.value?.prilohy?.findIndex(
    (prilohaZoppm: ZoPPMPriloha) =>
      prilohaZoppm?.povinnaPrilohaRef?.povinnaPrilohaId?.toString() ===
      props.priloha.povinnaPrilohaRef?.povinnaPrilohaId?.toString()
  )
)

const dokumenty = computed(
  () => formValues.value?.prilohy?.[idx.value]?.priloha?.dokumenty
)

const filesList = computed(() =>
  dokumenty.value?.map((value) => {
    return {
      key: `${value?.nazov}.${value?.format}`,
      value: '',
      id: value?.dokumentRef?.identifikatorUlozisko,
    }
  })
)

async function uploadFile(file: File[]) {
  loadingFile.value = true
  const checkedFiles = checkFiles(file)
  const promises: any[] = []
  const existingPrilohaIndex = formValues.value?.prilohy?.findIndex(
    (prilohaZoppm: ZoPPMPriloha) =>
      prilohaZoppm?.povinnaPrilohaRef?.povinnaPrilohaId?.toString() ===
      props.priloha.povinnaPrilohaRef?.povinnaPrilohaId?.toString()
  )
  const dokumentyTemp: Dokument[] =
    formValues.value?.prilohy?.[existingPrilohaIndex]?.priloha?.dokumenty || []

  // Pouzivatel moze nahrat aj viacero suborov, tak to prebehneme
  if (kodIrz?.value) {
    try {
      Array.from(checkedFiles).forEach((checkedFiles) => {
        promises.push(
          api.storeDokumentForZoppmDraftZoppmDraft(kodIrz.value, checkedFiles)
        )
      })

      await Promise.all(promises)
        .then((responses) => {
          responses.forEach(({ data }) => {
            notif.success(`Súbor ${data?.nazov}.${data?.format} bol úspešne nahraný`)

            dokumentyTemp.push({
              nazov: data?.nazov,
              format: data?.format,
              dokumentRef: data?.ref,
            })
          })
        })
        .catch((error) => {
          if (error?.response?.data?.exception) {
            notif.error(error.response.data.exception)
          }
        })

      if (
        existingPrilohaIndex === -1 ||
        (!existingPrilohaIndex && existingPrilohaIndex !== 0)
      ) {
        pushToPrilohy({
          povinnaPrilohaRef: props.priloha.povinnaPrilohaRef,
          priloha: {
            poradie: 1,
            nazov: props.priloha?.povinnaPrilohaRef?.nazov,
            dokumenty: dokumentyTemp as Dokument[],
          },
        })
      } else if (existingPrilohaIndex > -1) {
        updatePrilohy(existingPrilohaIndex, {
          povinnaPrilohaRef: props.priloha.povinnaPrilohaRef,
          priloha: {
            poradie: existingPrilohaIndex + 1,
            nazov: props.priloha?.povinnaPrilohaRef?.nazov,
            dokumenty: dokumentyTemp as Dokument[],
          },
        })
      }
    } catch (error) {
      notif.error('Súbor sa nepodarilo nahrať, chyba siete.')
      loadingFile.value = false
    }
  }
  loadingFile.value = false
}

async function handleFileRemove(identifikatorUlozisko: string, index: number) {
  const verziaUlozisko = dokumenty.value?.find(
    (file) => file?.dokumentRef?.identifikatorUlozisko === identifikatorUlozisko
  )?.dokumentRef?.verziaUlozisko

  const existingPrilohaIndex = formValues.value?.prilohy?.findIndex(
    (prilohaZoppm: ZoPPMPriloha) =>
      prilohaZoppm?.povinnaPrilohaRef?.povinnaPrilohaId?.toString() ===
      props.priloha.povinnaPrilohaRef?.povinnaPrilohaId?.toString()
  )

  if (kodIrz?.value) {
    try {
      await api
        .deleteContentOfDokumentForZoppmDraftZoppmDraft(
          kodIrz.value,
          identifikatorUlozisko,
          verziaUlozisko
        )
        .then(() => {
          const dokumentyTemp: Dokument[] = (
            formValues.value?.prilohy?.[existingPrilohaIndex]?.priloha?.dokumenty || []
          ).filter(
            (dokument: Dokument) =>
              dokument.dokumentRef?.identifikatorUlozisko !== identifikatorUlozisko
          )

          if (dokumentyTemp?.length === 0) {
            removePrilohy(existingPrilohaIndex)
          } else {
            updatePrilohy(existingPrilohaIndex, {
              povinnaPrilohaRef: props.priloha.povinnaPrilohaRef,
              priloha: {
                poradie: existingPrilohaIndex + 1,
                nazov: props.priloha?.povinnaPrilohaRef?.nazov,
                dokumenty: dokumentyTemp as Dokument[],
              },
            })
          }

          notif.success('Súbor zmazaný')
        })
    } catch {
      notif.error('Nastala chyba')
    }
  }
}

async function getDokument(identifikatorUlozisko: string) {
  const verziaUlozisko = dokumenty.value?.find(
    (file) => file?.dokumentRef?.identifikatorUlozisko === identifikatorUlozisko
  )?.dokumentRef?.verziaUlozisko

  if (kodIrz?.value) {
    try {
      await api
        .fetchContentOfDokumentForZoppmDraftZoppmDraft(
          kodIrz.value,
          identifikatorUlozisko,
          verziaUlozisko,
          undefined,
          undefined,
          {
            responseType: 'blob',
          }
        )
        .then((response) => {
          FileDownload(response.data, response.headers.filename)
        })
    } catch {}
  }
}

async function handleChceckboxFiles(isChecked: boolean) {
  if (isChecked) {
    if (dokumenty.value && kodIrz?.value) {
      const existingPrilohaIndex = formValues.value?.prilohy?.findIndex(
        (prilohaZoppm: ZoPPMPriloha) => {
          prilohaZoppm?.povinnaPrilohaRef?.povinnaPrilohaId?.toString() ===
            props.priloha.povinnaPrilohaRef?.povinnaPrilohaId?.toString()
        }
      )

      await Promise.all(
        dokumenty?.value?.map(async ({ dokumentRef }, index: number) => {
          await handleFileRemove(dokumentRef?.identifikatorUlozisko, index)
        })
      )
    }
  }
}
</script>

<template>
  <div class="govuk-inset-text">
    <IDSKCheckbox
      @is-checked="
        (value) => {
          isChecked = value

          handleChceckboxFiles(value)
        }
      "
    >
      <template #default>
        <div v-html="richTextToGovukHtml(t('zoppm.step13.previousUpload'))" />
      </template>
    </IDSKCheckbox>

    <IDSKParagraph v-html="richTextToGovukHtml(props.priloha.popis)"></IDSKParagraph>
    <IDSKParagraph>
      {{
        t('zoppm.step13.attachment2.part1.methodInfo') +
        t('zoppm.step0.prilohyTable.sposobPredlozenia.' + props.priloha.sposobPredlozenia)
      }}
    </IDSKParagraph>

    <div v-if="!isChecked">
      <UploadedFilesTable v-if="isReadOnly" />
      <IDSKSummaryList
        v-if="!isReadOnly"
        :data="filesList"
        action-text="Odstrániť"
        @action="handleFileRemove"
      >
        <template #link="{ value }">
          <IDSKLink
            href="#"
            no-visited-state
            @click.prevent="() => getDokument(value.id)"
          >
            {{ value.key }}
          </IDSKLink>
        </template>
      </IDSKSummaryList>

      <loading-spinner v-if="loadingFile" />

      <IDSKDragAndDrop
        :show-defaul-summary-list="isReadOnly ? true : false"
        :label="t('zoppm.step13.fileUploadLabel')"
        :hint="t('zoppm.step13.fileUploadHint') + ` (max. ${MAX_FILE_SIZE_MB}MB)`"
        @added-files="(files) => !isReadOnly && uploadFile(files)"
      />
    </div>
  </div>
</template>
